import { useParams } from "react-router-dom";
import useSWR from "swr";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/signed-avatar-image";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Section } from "@/components/section";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { ParticipatingTabs } from "@/components/prds/participating-tabs";
import SyncMyDocsDialog from "@/screens/User/sync-my-docs";
import { useAuthStore } from "@/lib/swr";
import type { User } from "@/lib/swr";

// User page: profile header + the PRDs this user participates in, one tab
// per identity (Owned first / default, then Member / Following).
// No Inbox here — that is home-only and self-only.
const Layout = () => {
  const { id } = useParams();
  const me = useAuthStore((store) => store.auth?.user);
  const { data: user, isLoading } = useSWR<User>(id ? [`/users/${id}`] : null);

  if (isLoading) return <ListLoading>Loading user…</ListLoading>;
  if (!user) return <ListEmpty>User not found.</ListEmpty>;

  return (
    <div className="space-y-6">
      <PageBreadcrumb parent={{ label: "Users" }} current={user.name} />

      <header className="flex items-center gap-3">
        <Avatar className="h-12 w-12">
          <SignedAvatarImage src={user.avatarThumb} alt={user.name} />
          <AvatarFallback>{user.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold leading-tight">
            {user.name}
          </h1>
          {user.email && (
            <div className="truncate text-sm text-muted-foreground">
              {user.email}
            </div>
          )}
        </div>
      </header>

      <Section
        title="PRDs"
        // Self-service only: syncing must run as the doc owner so the user
        // token can grant the app collaborator access on legacy docs.
        action={id && id === me?.id ? <SyncMyDocsDialog userId={id} /> : null}
      >
        {id && <ParticipatingTabs userId={id} includeOwned />}
      </Section>
    </div>
  );
};

export default Layout;
